<template>
  <div class="page-content">
    <div class="absolute-fullsize" v-if="loading" v-loading="loading"></div>
    <template v-else>
      <h1>{{ title }}</h1>

      <v-section top-indent>
        <v-toolbar>
          <v-button theme="danger" :disabled="isButtonDisabled" @click="handleClick">{{ button }}</v-button>
        </v-toolbar>

        <hr />

        <div class="table-wrapper">
          <v-action-log-keywords-table
            v-loading="loading"
            ref="keywordsTable"
            :data="list"
            :default-sort="sort"
            :selection-column="true"
            :reason-column="true"
            :cpl="accountCPL"
            :empty-text="$t('c.table.textEmpty')"
            @sort-change="handleChangeSort"
            @selection-change="selection = $event"
          />

          <v-row justify="center" class="pagination-wrapper" v-if="totalPage && totalPage > 1">
            <v-pagination
              layout="prev, pager, next"
              :page-size="1"
              :current-page="currentPage"
              :total="totalPage"
              :disabled="loading"
              @current-change="handleChangeCurrentPage"
            />
          </v-row>
        </div>
      </v-section>
    </template>
  </div>
</template>

<script>
import VActionLogKeywordsTable from '@/components/ActionLog/VActionLogKeywordsTable.vue'
import VPagination from '@/components/common/VPagination.vue'
import VToolbar from '@/components/common/VToolbar.vue'
import VSection from '@/components/common/VSection.vue'
import VRow from '@/components/common/VRow.vue'
import * as types from '@/store/types'
import * as authorities from '@/utils/authorities'
import storeUtils from '@/mixins/storeUtils'
import { templates } from '@/utils/notification'
import * as logTypes from '@/constants/logTypes'

export default {
  name: 'ActionLogKeywords',

  components: {
    VPagination,
    VActionLogKeywordsTable,
    VToolbar,
    VSection,
    VRow
  },

  mixins: [storeUtils],

  data() {
    return {
      logTypes,
      type: types.ACTION_LOG_KEYWORDS,
      selection: []
    }
  },

  computed: {
    accountCPL() {
      return this.$store.getters.accountCPL
    },
    loading() {
      return this.$store.state[this.type].loading
    },
    list() {
      return this.$store.state[this.type].list
    },
    sort() {
      return this.$store.state[this.type].params.sort
    },
    currentPage() {
      return this.$store.state[this.type].params.currentPage
    },
    totalPage() {
      return this.$store.state[this.type].params.totalPage
    },
    logType() {
      return this.$store.state[this.type].model.type
    },
    title() {
      if (
        [
          logTypes.KEYWORDS_WHITELIST_SWITCH,
          logTypes.KEYWORDS_WHITELIST_ADD
        ].includes(this.logType)
      ) {
        return this.$t('v.actionLogKeywords.titleAddedToExceptions')
      }
      if (this.logType === logTypes.KEYWORDS_WHITELIST_DELETE) {
        return this.$t('v.actionLogKeywords.titleRemovedFromExceptions')
      }
      if (this.logType === logTypes.KEYWORDS_PAUSE) {
        return this.$t('v.actionLogKeywords.titleDisabled')
      }
      if (this.logType === logTypes.KEYWORDS_WORST_AUTOPAUSE) {
        return this.$t('v.actionLogKeywords.titleAutodisabled')
      }

      // logType == logTypes.KEYWORDS_ENABLE
      return this.$t('v.actionLogKeywords.titleEnabled')
    },
    button() {
      if (
        [
          logTypes.KEYWORDS_WHITELIST_SWITCH,
          logTypes.KEYWORDS_WHITELIST_ADD
        ].includes(this.logType)
      ) {
        return this.$t('v.actionLogKeywords.buttonRemoveFromExceptions')
      }
      if (this.logType === logTypes.KEYWORDS_WHITELIST_DELETE) {
        return this.$t('v.actionLogKeywords.buttonAddToExceptions')
      }
      if (
        [logTypes.KEYWORDS_PAUSE, logTypes.KEYWORDS_WORST_AUTOPAUSE].includes(
          this.logType
        )
      ) {
        return this.$t('v.actionLogKeywords.buttonEnableAndAddToExceptions')
      }

      // logType == logTypes.KEYWORDS_ENABLE
      return this.$t('v.actionLogKeywords.buttonDisable')
    },
    isButtonDisabled() {
      return !this.canChangeKeywordsStatus
    },
    canChangeKeywordsStatus() {
      return this.$store.getters.hasAuthorities(
        authorities.CHANGE_KEYWORDS_STATUS
      )
    }
  },

  created() {
    // reset to default values
    const model = {
      id: this.$route.params.id,
      type: null
    }
    const params = {
      sort: {
        prop: null,
        order: null
      },
      limit: 10,
      currentPage: 1,
      totalPage: null
    }
    const list = []

    this.setActionLogKeywordsModel(model)
    this.setActionLogKeywordsParams(params)
    this.setActionLogKeywordsList(list)
    this.fetchActionLogKeywords()
  },

  destroyed() {
    this.clearKeywordsLog(types.ACTION_LOG_KEYWORDS)
  },

  methods: {
    handleChangeCurrentPage(value) {
      this.updateValue(this.type, { key: 'currentPage', value })
      this.fetchActionLogKeywords()
    },

    handleChangeSort({ prop, order }) {
      const { prop: oldProp, order: oldOrder } = this.$store.state[
        this.type
      ].params.sort

      if (prop !== oldProp || order !== oldOrder) {
        this.updateValue(this.type, { key: 'sort', value: { prop, order } })
        this.fetchActionLogKeywords()
      }
    },

    handleClick() {
      if (
        [
          logTypes.KEYWORDS_WHITELIST_SWITCH,
          logTypes.KEYWORDS_WHITELIST_ADD
        ].includes(this.logType)
      ) {
        this.changeExceptions('delete')
      } else if (
        [logTypes.KEYWORDS_PAUSE, logTypes.KEYWORDS_WORST_AUTOPAUSE].includes(
          this.logType
        )
      ) {
        this.enableAndAddToExceptions()
      } else if (this.logType === logTypes.KEYWORDS_WHITELIST_DELETE) {
        this.changeExceptions('add')
      } else {
        // logType == logTypes.KEYWORDS_ENABLE
        this.disableKeywords()
      }
    },

    setActionLogKeywordsModel(model) {
      this.setModel(this.type, model)
    },

    setActionLogKeywordsList(list) {
      this.setList(this.type, list)
    },

    setActionLogKeywordsParams(params) {
      this.setParams(this.type, params)
    },

    updateValue(type, { key, value }) {
      const params = {
        [key]: value
      }

      this.updateParams(type, params)
    },

    fetchActionLogKeywords() {
      this.$store.dispatch(types.FETCH_ACTION_LOG_ITEM, { name: this.type })
    },

    disableKeywords() {
      this.setKeywordsStatus('PAUSED')
    },

    enableAndAddToExceptions() {
      const { table } = this.$refs.keywordsTable.$refs
      const { type: name, selection } = this
      const criterions = selection
        .filter(item => item.keywordState !== 'removed')
        .map(item => item.id)

      if (criterions.length) {
        this.$store
          .dispatch(types.CHANGE_KEYWORDS_STATUS_FROM_ACTION_LOG, {
            name,
            criterions
          })
          .then((result) => {
            this.notifyUserAboutChangedExceptionKeywords('toggle', result)
            table.clearSelection()
          })
      }
    },

    changeExceptions(action) {
      const { table } = this.$refs.keywordsTable.$refs
      const selection = this.selection || []
      const during = selection[0] && selection[0].during ? selection[0].during : null

      const keywords = selection
        .filter(item => item.keywordState !== 'removed')
        .map((item) => {
          if (action === 'add') {
            return {
              keyword_id: item.keywordId,
              ad_group_id: item.adGroupId
            }
          }
          return item.id
        })

      if (keywords.length) {
        this.$store
          .dispatch(types.CHANGE_EXCEPTION_KEYWORDS, {
            keywords,
            during,
            action
          })
          .then((result) => {
            this.notifyUserAboutChangedExceptionKeywords(action, result)
            table.clearSelection()
          })
      }
    },

    setKeywordsStatus(status) {
      const { table } = this.$refs.keywordsTable.$refs
      const { type: name, selection } = this
      const keywords = selection
        .filter(item => item.keywordState !== 'removed')
        .map(item => [item.keywordId, item.adGroupId])

      if (keywords.length) {
        this.$store
          .dispatch(types.CHANGE_KEYWORDS_STATUS, { name, keywords, status })
          .then((response) => {
            this.notifyUserAboutChangedKeywordsStatus(
              status,
              response.keywords
            )
            table.clearSelection()
          })
      }
    },

    notifyUserAboutChangedKeywordsStatus(status, adList) {
      if (adList.length) {
        this.$store.dispatch(types.PUSH_NOTIFICATION, {
          status,
          list: adList,
          template: templates.KEYWORDS_CHANGED_STATUS
        })
      }
    },

    notifyUserAboutChangedExceptionKeywords(action, result) {
      const { keywords, count, logId } = result

      if (!keywords.length) return

      const actionMap = {
        add: templates.KEYWORDS_ADDED_TO_EXCEPTIONS,
        delete: templates.KEYWORDS_REMOVED_FROM_EXCEPTIONS,
        toggle: templates.KEYWORDS_ENABLED_AND_ADDED_TO_EXCEPTIONS
      }

      const notificationTemplate = actionMap[action]

      this.$store.dispatch(types.PUSH_NOTIFICATION, {
        logId,
        count,
        list: keywords,
        template: notificationTemplate
      })
    },

    clearKeywordsLog(name) {
      this.setList(name, [])
      this.setModel(name, {
        id: null,
        type: null
      })
      this.updateParams(name, {
        sort: {
          prop: null,
          order: null
        },
        currentPage: 1,
        totalPage: null
      })
    }
  }
}
</script>
